import { supabase } from '@/lib/supabase/client'
import { Canvg } from 'canvg'
import type { jsPDF } from 'jspdf'
import { resolveSignatureUrl } from '@/utils/signatureUrl'

export const STM_LOGO_SVG = `<svg width="12662" height="3631" viewBox="0 0 12662 3631" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M3824.18 2586.89C3698.69 2586.89 3590.68 2568 3500.16 2530.22C3409.64 2492.44 3326.32 2443.54 3250.2 2383.54L3105.16 2543.55H3012.59V1843.49H3108.25C3132.94 1952.39 3173.05 2054.62 3228.6 2150.18C3286.2 2245.75 3363.35 2323.53 3460.04 2383.54C3556.73 2441.32 3675.03 2470.21 3814.92 2470.21C3930.13 2470.21 4025.79 2450.21 4101.91 2410.21C4180.09 2370.2 4238.72 2315.75 4277.81 2246.86C4318.95 2175.74 4339.53 2097.96 4339.53 2013.51C4339.53 1926.83 4321.01 1856.83 4283.98 1803.49C4246.95 1750.15 4198.6 1709.04 4138.94 1680.14C4081.34 1649.03 4018.59 1624.58 3950.7 1606.8C3884.87 1589.02 3822.12 1572.36 3762.46 1556.8C3618.45 1516.8 3491.93 1464.57 3382.89 1400.12C3273.86 1333.45 3188.48 1247.88 3126.76 1143.43C3067.1 1038.98 3037.27 908.967 3037.27 753.398C3037.27 582.272 3071.22 441.149 3139.11 330.028C3207 218.908 3296.49 136.678 3407.58 83.3404C3518.67 27.7801 3636.97 0 3762.46 0C3855.04 0 3937.33 14.4456 4009.33 43.3369C4083.4 72.2283 4161.57 114.454 4243.86 170.015L4364.21 30.0026H4459.88V710.061H4367.3C4307.64 498.932 4227.4 347.807 4126.6 256.689C4025.79 163.347 3902.36 116.677 3756.29 116.677C3667.83 116.677 3586.56 132.233 3512.5 163.347C3440.5 192.239 3382.89 234.464 3339.69 290.025C3296.49 345.585 3274.89 411.146 3274.89 486.708C3274.89 557.825 3293.4 616.719 3330.43 663.39C3367.46 707.838 3415.81 743.397 3475.47 770.066C3535.13 796.735 3598.91 818.959 3666.8 836.738C3734.69 854.518 3799.49 871.186 3861.21 886.743C4017.56 926.746 4149.23 982.306 4256.21 1053.42C4363.18 1122.32 4444.45 1213.44 4499.99 1326.78C4555.54 1440.12 4583.31 1582.36 4583.31 1753.48C4583.31 2015.73 4517.48 2220.19 4385.81 2366.87C4256.21 2513.55 4068.99 2586.89 3824.18 2586.89Z" fill="#3A3A3A"/>
  <path d="M5149.07 2543.55V2443.54H5534.81V130.011H5380.51C5267.36 130.011 5169.64 163.347 5087.35 230.02C5007.12 296.692 4944.37 384.477 4899.11 493.376C4855.91 602.274 4833.28 720.062 4831.22 846.739H4707.79V30.0026H6849.41V846.739H6725.97C6723.91 720.062 6700.25 602.274 6654.99 493.376C6611.79 384.477 6550.07 296.692 6469.84 230.02C6389.6 163.347 6291.88 130.011 6176.68 130.011H6022.38V2443.54H6386.52V2543.55H5149.07Z" fill="#3A3A3A"/>
  <path d="M8104.24 2573.55L7443.85 263.356H7431.51V2443.54H7770.96V2543.55H6956.28V2443.54H7295.73V130.011H6956.28V30.0026H7931.43L8366.54 1546.8H8378.88L8814 30.0026H9708.91V130.011H9387.97V2443.54H9708.91V2543.55H8570.21V2443.54H8900.4V263.356H8888.06L8230.76 2573.55H8104.24Z" fill="#3A3A3A"/>
  <path d="M6331 3631L0 2941.29H12662L6331 3631Z" fill="#FB4B4E"/>
</svg>`

export function formatDateDDMMYYYY(dateString: string): string {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB')
}

export function getFullName(name: string): string {
  return name
}

export async function getLogoDataUrl(): Promise<string> {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  canvas.width = 400
  canvas.height = 120

  try {
    const v = await Canvg.fromString(ctx, STM_LOGO_SVG)
    v.resize(400, 120, 'xMidYMid meet')
    await v.render()
    return canvas.toDataURL('image/png')
  } catch {
    return ''
  }
}

/**
 * Looks up a profile by email or full name. Names are matched ignoring case
 * and surrounding whitespace, since some stored names have stray spaces.
 */
export async function findProfileByName<T>(nameOrEmail: string, columns: string): Promise<T | null> {
  const trimmed = nameOrEmail?.trim()
  if (!trimmed) return null
  const column = trimmed.includes('@') ? 'email' : 'full_name'

  const { data: exact } = await supabase
    .from('profiles')
    .select(columns)
    .eq(column, nameOrEmail)
    .limit(2)
  if (exact?.length === 1) return exact[0] as T

  const { data: loose } = await supabase
    .from('profiles')
    .select(`${column}, ${columns}`)
    .ilike(column, `%${trimmed}%`)
    .limit(10)
  const matches = ((loose || []) as unknown as Record<string, unknown>[]).filter(
    (row) => String(row[column] ?? '').trim().toLowerCase() === trimmed.toLowerCase(),
  )
  return matches.length === 1 ? (matches[0] as T) : null
}

export async function getCertificateNumber(fullName: string): Promise<string | null> {
  try {
    const inspector = await findProfileByName<{ certificate_no: string | null }>(fullName, 'certificate_no')
    const certNo = (inspector?.certificate_no || '').replace(/\s+/g, '')
    return certNo || null
  } catch {
    return null
  }
}

async function getOptimizedImage(imgUrl: string): Promise<string | null> {
  if (!imgUrl || typeof imgUrl !== 'string' || !imgUrl.trim()) return null
  if (imgUrl.startsWith('data:')) return imgUrl

  return new Promise((resolve) => {
    const img = new Image()
    img.setAttribute('crossOrigin', 'anonymous')

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const maxDimension = 200
      let width = img.width
      let height = img.height

      if (width > height && width > maxDimension) {
        height = (height * maxDimension) / width
        width = maxDimension
      } else if (height > maxDimension) {
        width = (width * maxDimension) / height
        height = maxDimension
      }

      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0, width, height)
      try {
        resolve(canvas.toDataURL('image/png'))
      } catch {
        resolve(null)
      }
    }

    img.onerror = () => resolve(null)
    img.src = imgUrl
  })
}

export async function getSignature(fullName: string): Promise<string | null> {
  try {
    const profile = await findProfileByName<{
      signature: string | null
      inspectors: { signature?: string | null } | { signature?: string | null }[] | null
    }>(fullName, 'signature, inspectors(signature)')

    const inspectors = profile?.inspectors
    const inspectorSignature = Array.isArray(inspectors) ? inspectors[0]?.signature : inspectors?.signature
    const signatureUrl = profile?.signature || inspectorSignature
    if (!signatureUrl) return null
    return getOptimizedImage(await resolveSignatureUrl(signatureUrl))
  } catch {
    return null
  }
}

export function getBase64Image(imgUrl: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.setAttribute('crossOrigin', 'anonymous')
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      canvas.getContext('2d')?.drawImage(img, 0, 0)
      try {
        resolve(canvas.toDataURL('image/png'))
      } catch {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = imgUrl
  })
}

export const NDT_TABLE_OPTIONS = {
  theme: 'plain' as const,
  styles: {
    fontSize: 8,
    textColor: [0, 0, 0] as [number, number, number],
    cellPadding: 0.5,
    lineColor: [100, 100, 100] as [number, number, number],
    lineWidth: { top: 0, right: 0.1, bottom: 0, left: 0.1 },
    minCellHeight: 1.5,
  },
  headStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [68, 68, 68] as [number, number, number],
    fontStyle: 'normal' as const,
    fontSize: 6.5,
    cellPadding: 0.3,
    lineWidth: { top: 0.1, right: 0.1, bottom: 0, left: 0.1 },
    lineColor: [100, 100, 100] as [number, number, number],
    halign: 'left' as const,
    valign: 'middle' as const,
  },
  bodyStyles: {
    textColor: [0, 0, 0] as [number, number, number],
    lineWidth: { top: 0, right: 0.1, bottom: 0, left: 0.1 },
  },
  tableLineColor: [100, 100, 100] as [number, number, number],
  tableLineWidth: 0.1,
}

export function addNdtFooter(doc: jsPDF) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
    doc.setFontSize(7)
    doc.text(
      'STM Inspection AS. NO 997 553 608 MVA. Phone: +47 55 94 30 90. Web: stmi.no email: inspection@stmas.no',
      pageWidth / 2,
      pageHeight - 5,
      { align: 'center' },
    )
  }
}
